import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adventuresApi } from '@api/endpoints/adventures'
import { AdventureListParams, CreateAdventureRequest, UpdateAdventureRequest, AdventureDto, AdventureSearchRequest, AdventureJoinRequest } from '@api/types/adventure'
import { getCachedSearchResults, cacheSearchResults } from '@utils/adventureCache'

// Transform API response to match our expected AdventureDto format
function transformAdventure(apiData: any): AdventureDto {
  // Build location object from top-level latitude/longitude fields
  // Check for both exact location (latitude/longitude) and approximate location (approxLatitude/approxLongitude)
  let location = undefined

  if (apiData.latitude !== undefined && apiData.longitude !== undefined) {
    location = {
      latitude: apiData.latitude,
      longitude: apiData.longitude,
      address: typeof apiData.location === 'string' ? apiData.location : undefined,
    }
  } else if (apiData.approxLatitude !== undefined && apiData.approxLongitude !== undefined) {
    location = {
      latitude: apiData.approxLatitude,
      longitude: apiData.approxLongitude,
      address: typeof apiData.location === 'string' ? apiData.location : undefined,
    }
  }

  // Extract all fields, including editor-specific ones
  const transformed: AdventureDto = {
    id: apiData.id,
    title: apiData.title || apiData.name || '',
    description: apiData.description || apiData.fullDescription || apiData.quickDescription || '',
    fullDescription: apiData.fullDescription || apiData.description || '',
    quickDescription: apiData.quickDescription || '',
    difficulty: apiData.difficultyLevel || apiData.difficulty || 1,
    difficultyLevel: apiData.difficultyLevel || apiData.difficulty || 1,
    // Keep numeric status from API (AdventureStatus enum: 1=Draft, 2=Testing, 3=Published, 4=ModeratorReview, 5=Archived, 6=Suspended)
    status: apiData.status ?? 1, // Default to Draft (1) if undefined
    createdBy: apiData.creatorId || apiData.creator?.id || apiData.owner?.entityId || apiData.createdBy || '',
    createdByName: apiData.creatorName || apiData.createdByUserName || apiData.creator?.displayName || apiData.owner?.entityName || apiData.createdByName || 'Unknown',
    // Owner entity (new API format)
    owner: apiData.owner,
    createdDate: apiData.createdDate || apiData.createdAt || new Date().toISOString(),
    modifiedDate: apiData.modifiedDate || apiData.updatedAt || new Date().toISOString(),
    imageUrl: apiData.imageUrl || apiData.pictureUrl,
    thumbnailUrl: apiData.thumbnailUrl || apiData.thumbnailImageUrl,
    coverUrl: apiData.coverUrl || apiData.coverImageUrl,
    thumbnailImageUrl: apiData.thumbnailImageUrl || apiData.thumbnailUrl,
    coverImageUrl: apiData.coverImageUrl || apiData.coverUrl,
    tags: apiData.tags || [],
    participantCount: apiData.participantCount || 0,
    completionCount: apiData.completionCount || 0,
    averageRating: apiData.averageRating || (typeof apiData.rating === 'number' ? apiData.rating : apiData.rating?.overallAverageScore) || undefined,
    rating: typeof apiData.rating === 'object' ? apiData.rating : undefined,
    isPublic: apiData.isPublic !== undefined ? apiData.isPublic : true,
    location,
    // Editor-specific fields
    // API returns categoryId, we map it to mainCategory for internal use
    mainCategory: apiData.categoryId ?? apiData.mainCategory,
    startDate: apiData.startDate,
    endDate: apiData.endDate,
    latitude: apiData.latitude,
    longitude: apiData.longitude,
    allowGroups: apiData.allowGroups !== undefined ? apiData.allowGroups : false,
    groupPlayStatus: apiData.groupPlayStatus ?? (apiData.allowGroups ? 1 : 0),
    maxParticipants: apiData.maxParticipants !== undefined ? apiData.maxParticipants : 0,
    isRepeatable: apiData.isRepeatable !== undefined ? apiData.isRepeatable : false,
    // Group participation settings
    groupParticipationSettings: apiData.groupParticipationSettings,
    // Silent adventure fields
    isSilentJoin: apiData.isSilentJoin,
    isSilentComplete: apiData.isSilentComplete,
    silentCompleteDurationMinutes: apiData.silentCompleteDurationMinutes,
    // Eligibility fields (from search results)
    isEligible: apiData.isEligible,
    meetsDisplayCriteria: apiData.meetsDisplayCriteria,
    meetsJoinCriteria: apiData.meetsJoinCriteria,
    joinCriteriaFailureReasons: apiData.joinCriteriaFailureReasons,
    requiresSubscription: apiData.requiresSubscription,
    approxLatitude: apiData.approxLatitude,
    approxLongitude: apiData.approxLongitude,
    // User's relationship to this adventure (bitmask)
    userRelationType: apiData.userRelationType,
    // Session-related fields
    isStarted: apiData.isStarted,
    isCompleted: apiData.isCompleted,
    sessions: apiData.sessions,
    totalSessionCount: apiData.totalSessionCount,
    // Management permissions
    canManage: apiData.canManage,
    creatorId: apiData.creatorId,
    // Geocache code fields
    geocacheCode: apiData.geocacheCode,
    geocacheCodeUrl: apiData.geocacheCodeUrl,
    // Moderation features
    hasModerationNodes: apiData.hasModerationNodes,
    // Available group sessions count
    availableGroupSessionCount: apiData.availableGroupSessionCount,
    // Test-mode signal: true when adventure has a Testing workflow version and the
    // current user is creator/maintainer/tester. Drives the "Testing" pill + "Test Solo"
    // button on AdventureDetailView so testers see they're running the testing flow.
    hasTestVersion: apiData.hasTestVersion,
    testingWorkflowVersion: apiData.testingWorkflowVersion,
    publishedWorkflowVersion: apiData.publishedWorkflowVersion,
    latestWorkflowVersion: apiData.latestWorkflowVersion,
  }

  return transformed
}

// Helper to clean params for query keys
function cleanParamsForKey(params?: AdventureListParams): AdventureListParams | undefined {
  if (!params) return undefined

  const cleaned: any = {}
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      cleaned[key] = value
    }
  })

  return Object.keys(cleaned).length > 0 ? cleaned : undefined
}

// Query keys
export const adventureKeys = {
  all: ['adventures'] as const,
  lists: () => [...adventureKeys.all, 'list'] as const,
  list: (params?: AdventureListParams) => [...adventureKeys.lists(), cleanParamsForKey(params)] as const,
  searches: () => [...adventureKeys.all, 'search'] as const,
  search: (request?: AdventureSearchRequest) => [...adventureKeys.searches(), cleanParamsForKey(request as any)] as const,
  details: () => [...adventureKeys.all, 'detail'] as const,
  detail: (id: string) => [...adventureKeys.details(), id] as const,
  edits: () => [...adventureKeys.all, 'edit'] as const,
  edit: (id: string) => [...adventureKeys.edits(), id] as const,
}

// Get list of adventures
export function useAdventures(params?: AdventureListParams) {
  return useQuery({
    queryKey: adventureKeys.list(params),
    queryFn: async () => {
      const result = await adventuresApi.getAdventures(params)

      // Handle array response - transform to PaginatedResponse
      if (Array.isArray(result)) {
        // Transform each adventure to match our expected format
        const transformedItems = result.map(transformAdventure)
        const totalCount = transformedItems.length
        const pageSize = params?.pageSize || 12
        const pageNumber = params?.page || 1
        const totalPages = Math.ceil(totalCount / pageSize)

        return {
          items: transformedItems,
          totalCount,
          pageNumber,
          pageSize,
          totalPages,
          hasNextPage: pageNumber < totalPages,
          hasPreviousPage: pageNumber > 1,
        }
      }

      // Handle wrapped response (ApiResponse<PaginatedResponse<T>>)
      if (result && typeof result === 'object' && 'data' in result) {
        const data = (result as any).data

        // If data.items exists, transform each item
        if (data.items && Array.isArray(data.items)) {
          return {
            ...data,
            items: data.items.map(transformAdventure)
          }
        }

        return data
      }

      // Already in correct format - but still transform items if it's a paginated response
      if (result && typeof result === 'object' && 'items' in result) {
        const paginatedResult = result as any
        return {
          ...paginatedResult,
          items: paginatedResult.items.map(transformAdventure)
        }
      }

      return result
    },
  })
}

// Search adventures with advanced filters
export function useSearchAdventures(searchRequest?: AdventureSearchRequest) {
  return useQuery({
    queryKey: adventureKeys.search(searchRequest),
    queryFn: async () => {
      // If no search request provided, return empty results
      if (!searchRequest) {
        console.log('useSearchAdventures: No search request, returning empty')
        return {
          items: [],
          totalCount: 0,
          pageNumber: 1,
          pageSize: 20,
          totalPages: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        }
      }

      console.log('useSearchAdventures: Making API call with:', searchRequest)

      try {
        const result = await adventuresApi.searchAdventures(searchRequest)
        console.log('useSearchAdventures: API response:', result)

        let transformedResult

        // Handle array response - transform to PaginatedResponse
        if (Array.isArray(result)) {
          // Transform each adventure to match our expected format
          const transformedItems = result.map(transformAdventure)
          const totalCount = transformedItems.length
          const pageSize = searchRequest?.pageSize || 20
          const pageNumber = searchRequest?.page || 1
          const totalPages = Math.ceil(totalCount / pageSize)

          transformedResult = {
            items: transformedItems,
            totalCount,
            pageNumber,
            pageSize,
            totalPages,
            hasNextPage: pageNumber < totalPages,
            hasPreviousPage: pageNumber > 1,
          }
        }
        // Handle wrapped response (ApiResponse<PaginatedResponse<T>>)
        else if (result && typeof result === 'object' && 'data' in result) {
          const data = (result as any).data

          // If data.items exists, transform each item
          if (data.items && Array.isArray(data.items)) {
            transformedResult = {
              ...data,
              items: data.items.map(transformAdventure)
            }
          } else {
            transformedResult = data
          }
        }
        // Handle response with 'adventures' field (from search endpoint)
        else if (result && typeof result === 'object' && 'adventures' in result) {
          const paginatedResult = result as any
          console.log('useSearchAdventures: Transforming adventures array')
          transformedResult = {
            items: paginatedResult.adventures.map(transformAdventure),
            pageNumber: paginatedResult.page,
            totalCount: paginatedResult.totalCount,
            pageSize: paginatedResult.pageSize,
            totalPages: paginatedResult.totalPages,
            hasNextPage: paginatedResult.hasNextPage,
            hasPreviousPage: paginatedResult.hasPreviousPage,
          }
        }
        // Already in correct format - but still transform items if it's a paginated response
        else if (result && typeof result === 'object' && 'items' in result) {
          const paginatedResult = result as any
          transformedResult = {
            ...paginatedResult,
            items: paginatedResult.items.map(transformAdventure)
          }
        } else {
          transformedResult = result
        }

        // Cache the successful result
        if (transformedResult) {
          // Fire and forget - don't await
          cacheSearchResults(searchRequest, transformedResult).catch((err) => {
            console.warn('Failed to cache search results:', err)
          })
        }

        return transformedResult
      } catch (error) {
        // On error, try to use cached data as fallback
        console.warn('useSearchAdventures: API error, checking cache...', error)
        const cached = await getCachedSearchResults(searchRequest)

        if (cached) {
          console.log('📦 Using cached data as fallback')
          return cached
        }

        // If no cache available, re-throw the error
        throw error
      }
    },
    enabled: !!searchRequest,
    // Treat cached data as stale so it always refetches
    staleTime: 0,
    // Keep cached data in React Query for 30 minutes
    gcTime: 1000 * 60 * 30,
    // Refresh search results every 30 seconds
    refetchInterval: 30000,
  })
}

// Get single adventure (for viewing)
export function useAdventure(id: string) {
  return useQuery({
    queryKey: adventureKeys.detail(id),
    queryFn: async () => {
      const result = await adventuresApi.getAdventure(id)

      let adventure = result

      // Handle wrapped response (ApiResponse<AdventureDto>)
      if (result && typeof result === 'object' && 'data' in result && result.data) {
        adventure = (result as any).data
      }

      // Transform the adventure to match our expected format
      if (adventure && typeof adventure === 'object') {
        return transformAdventure(adventure)
      }

      return adventure
    },
    enabled: !!id,
  })
}

// Get adventure for editing (includes all editor fields like mainCategory)
export function useAdventureForEdit(id: string) {
  return useQuery({
    queryKey: adventureKeys.edit(id),
    queryFn: async () => {
      const result = await adventuresApi.getAdventureForEdit(id)

      let adventure = result

      // Handle wrapped response (ApiResponse<AdventureDto>)
      if (result && typeof result === 'object' && 'data' in result && result.data) {
        adventure = (result as any).data
      }

      // Transform the adventure to match our expected format
      if (adventure && typeof adventure === 'object') {
        return transformAdventure(adventure)
      }

      return adventure
    },
    enabled: !!id,
  })
}

// Create adventure
export function useCreateAdventure() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateAdventureRequest) => adventuresApi.createAdventure(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adventureKeys.lists() })
    },
  })
}

// Update adventure
export function useUpdateAdventure() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateAdventureRequest | (Omit<UpdateAdventureRequest, 'categoryId'> & { mainCategory?: number }) }) => {
      // Transform mainCategory to categoryId for API compatibility
      // The internal data model uses mainCategory but API expects categoryId
      const apiData: UpdateAdventureRequest = {
        ...data,
        categoryId: 'categoryId' in data ? data.categoryId : (data as any).mainCategory,
      }
      // Remove mainCategory if it was passed (shouldn't be sent to API)
      delete (apiData as any).mainCategory

      const result = await adventuresApi.updateAdventure(id, apiData)

      // Handle wrapped response (ApiResponse<AdventureDto>)
      let adventure = result
      if (result && typeof result === 'object' && 'data' in result && result.data) {
        adventure = (result as any).data
      }

      // Transform the adventure to match our expected format
      if (adventure && typeof adventure === 'object') {
        return transformAdventure(adventure)
      }

      return adventure
    },
    onSuccess: (transformedAdventure, variables) => {
      // Update the cache with the transformed adventure (for both detail and edit)
      if (transformedAdventure) {
        queryClient.setQueryData(
          adventureKeys.detail(variables.id),
          transformedAdventure
        )
        queryClient.setQueryData(
          adventureKeys.edit(variables.id),
          transformedAdventure
        )
      }

      queryClient.invalidateQueries({ queryKey: adventureKeys.lists() })
    },
  })
}

// Delete adventure
export function useDeleteAdventure() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => adventuresApi.deleteAdventure(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adventureKeys.lists() })
    },
  })
}

// Join adventure
export function useJoinAdventure() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => adventuresApi.joinAdventure(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: adventureKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: adventureKeys.lists() })
    },
  })
}

// Join adventure solo (explicit)
export function useJoinAdventureSolo() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, startDateTime, isTestSession }: { id: string; startDateTime?: string; isTestSession?: boolean }) =>
      adventuresApi.joinAdventureSolo(id, startDateTime, isTestSession),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: adventureKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: adventureKeys.lists() })
    },
  })
}

// Create group and join adventure
export function useCreateGroupJoin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ adventureId, request }: { adventureId: string; request: Omit<import('@api/types/adventure').AdventureJoinRequest, 'adventureId'> }) =>
      adventuresApi.createGroupJoin(adventureId, request),
    onSuccess: (_, { adventureId }) => {
      queryClient.invalidateQueries({ queryKey: adventureKeys.detail(adventureId) })
      queryClient.invalidateQueries({ queryKey: adventureKeys.lists() })
      // Also invalidate any group-related queries
      queryClient.invalidateQueries({ queryKey: ['groups'] })
      queryClient.invalidateQueries({ queryKey: ['my-groups'] })
    },
  })
}

// Complete adventure
export function useCompleteAdventure() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => adventuresApi.completeAdventure(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: adventureKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: adventureKeys.lists() })
    },
  })
}

// Join with existing group
export function useJoinWithGroup() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ adventureId, request }: { adventureId: string; request: Omit<AdventureJoinRequest, 'adventureId'> }) =>
      adventuresApi.joinWithGroup(adventureId, request),
    onSuccess: (_, { adventureId }) => {
      queryClient.invalidateQueries({ queryKey: adventureKeys.detail(adventureId) })
    },
  })
}

// Join public queue
export function useJoinPublic() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ adventureId, request }: { adventureId: string; request: Omit<AdventureJoinRequest, 'adventureId'> }) =>
      adventuresApi.joinPublic(adventureId, request),
    onSuccess: (_, { adventureId }) => {
      queryClient.invalidateQueries({ queryKey: adventureKeys.detail(adventureId) })
    },
  })
}

// Create invite/public session
export function useCreateInviteSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ adventureId, request }: { adventureId: string; request: Omit<AdventureJoinRequest, 'adventureId'> }) =>
      adventuresApi.createSession(adventureId, request),
    onSuccess: (_, { adventureId }) => {
      queryClient.invalidateQueries({ queryKey: adventureKeys.detail(adventureId) })
    },
  })
}

// Join an existing public session
export function useJoinPublicSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (sessionId: string) =>
      adventuresApi.joinPublicSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adventureKeys.all })
    },
  })
}

// Get public sessions for an adventure
export function usePublicSessions(adventureId: string, enabled = true) {
  return useQuery({
    queryKey: [...adventureKeys.detail(adventureId), 'public-sessions'] as const,
    queryFn: () => adventuresApi.getPublicSessions(adventureId),
    enabled: !!adventureId && enabled,
    staleTime: 15000,
  })
}

// Get adventure categories
export function useAdventureCategories() {
  return useQuery({
    queryKey: ['adventure-categories'],
    queryFn: async () => {
      const result = await adventuresApi.getCategories()

      // Handle wrapped response (ApiResponse<AdventureCategoryDto[]>)
      if (result && typeof result === 'object' && 'data' in result) {
        return (result as any).data
      }

      return result
    },
    staleTime: 1000 * 60 * 60, // Categories don't change often, cache for 1 hour
  })
}
